class FgwpFlask extends HTMLElement {
  constructor() {
    super();

    // Component state
    this.cartUpdateUnsubscriber = undefined;
    this.cartTotal = 0;
    this.fgwpGoal = parseFloat(this.dataset.fgwpGoal) || 0;
    this.fgwpCurrent = parseFloat(this.dataset.fgwpCurrent) || 0;
    this.fgwpRemaining = parseFloat(this.dataset.fgwpRemaining) || this.fgwpGoal;
    // Normalize variantId as string
    this.variantId = this.dataset.variantId ? this.dataset.variantId.toString() : '';
    this.isAddingToCart = false;
    this.giftInCart = false;
    // Bind click handler for proper removal
    this.boundAddGiftClick = this.addGiftToCart.bind(this);

    // CSS variables
    this.style.setProperty('--fgwp-progress-percent', '0%');
  }

  /**
   * Initialize component when connected to DOM
   */
  connectedCallback() {
    this.progressBar = this.querySelector('.fgwp__progress-bar');
    this.currentText = this.querySelector('.fgwp__progress-text__current');
    this.addGiftButton = this.querySelector('.fgwp__add-gift-button');

    // Add gift button click event
    if (this.addGiftButton) {
      // disable until cart check
      this.addGiftButton.disabled = true;
      // hide if no valid goal
      if (this.fgwpGoal <= 0 || isNaN(this.fgwpGoal)) {
        this.addGiftButton.style.display = 'none';
      }
      this.addGiftButton.addEventListener('click', this.boundAddGiftClick);
    }

    // Subscribe to cart update events
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, function handleCartUpdate(event) {
      this.handleCartUpdate(event);
    }.bind(this));

    // Initial cart check
    this.getCartTotal()
      .then(function processCartTotal(cartTotal) {
        this.updateProgressBar(cartTotal);
      }.bind(this))
      .catch(function handleError(error) {
        console.error('Error calculating initial cart total:', error);
      });
  }

  /**
   * Clean up event listeners when disconnected from DOM
   */
  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }

    if (this.addGiftButton) {
      this.addGiftButton.removeEventListener('click', this.boundAddGiftClick);
    }
  }

  /**
   * Handle cart update events
   * @param {Object} event - Cart update event object
   */
  handleCartUpdate(event) {
    console.log('Received cart update event:', event);
    this.getCartTotal()
      .then(function processCartTotal(cartTotal) {
        this.updateProgressBar(cartTotal);
      }.bind(this))
      .catch(function handleError(error) {
        console.error('Error calculating cart total after update:', error);
      });
  }

  /**
   * Calculate total cart value excluding free gifts
   * @returns {Promise<number>} - Promise resolving to cart total in cents
   */
  async getCartTotal() {
    try {
      // Fetch current cart data directly from cart.js endpoint
      const response = await fetch(`/cart.js`);
      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      }

      const cartData = await response.json();
      let cartTotal = 0;

      // Calculate cart total excluding free gifts
      if (cartData.items) {
        cartTotal = cartData.total_price / 100;

        // Check if free gift is already in cart
        this.checkGiftInCart(cartData);
      }

      return cartTotal;
    } catch (error) {
      console.error('Error fetching cart data:', error);
      return 0; // Return 0 as fallback if we can't get the cart data
    }
  }

  /**
   * Check if the free gift is already in the cart
   * @param {Object} cartData - Cart data from fetch response
   */
  checkGiftInCart(cartData) {
    if (!cartData.items || !this.variantId) return;

    this.giftInCart = cartData.items.some(function checkItemVariant(item) {
      // Compare variant IDs, ensuring type consistency (variantId is string)
      return item.variant_id.toString() === this.variantId;
    }.bind(this));

    // Update button text if it exists
    if (this.addGiftButton){
      if (this.giftInCart) {
        this.addGiftButton.textContent = 'Gift is in Cart';
        this.addGiftButton.disabled = true;
      } else {
        this.addGiftButton.textContent = 'Add Your Free Gift';
        this.addGiftButton.disabled = false;
      }
    }
  }

  /**
   * Update the progress bar based on cart total
   * @param {number} cartTotal - Current cart total in cents
   */
  updateProgressBar(cartTotal) {
    if (this.fgwpGoal <= 0 || isNaN(this.fgwpGoal)) {
      if (this.addGiftButton) {
        this.addGiftButton.style.display = 'none';
        this.addGiftButton.disabled = true; // Ensure disabled if goal invalid
      }
      return;
    }

    // Update component state
    this.cartTotal = cartTotal;
    this.fgwpCurrent = Math.min(cartTotal, this.fgwpGoal);
    this.fgwpRemaining = Math.max(0, this.fgwpGoal - this.fgwpCurrent);

    // Update dataset values
    this.dataset.fgwpCurrent = this.fgwpCurrent;
    this.dataset.fgwpRemaining = this.fgwpRemaining;

    // Calculate progress percentage
    const progressPercentage = Math.min(100, (this.fgwpCurrent / this.fgwpGoal) * 100);

    // Update progress bar
    if (this.progressBar) {
      this.progressBar.value = this.fgwpCurrent;
    }

    this.style.setProperty('--fgwp-progress-percent', `${progressPercentage}%`);

    // Update text display
    if (this.currentText) {
      this.currentText.textContent = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
      }).format(this.fgwpCurrent);
    }

    // Show or hide add gift button based on threshold
    if (this.addGiftButton) {
      if (this.fgwpCurrent >= this.fgwpGoal) {
        this.addGiftButton.style.display = 'block';

        // Set appropriate button text and state based on gift state
        if (this.giftInCart) {
          this.addGiftButton.textContent = 'Gift is in Cart';
          this.addGiftButton.disabled = true; // Disable if gift is already in cart
        } else {
          this.addGiftButton.textContent = 'Add Your Free Gift';
          this.addGiftButton.disabled = false; // Enable if goal met and gift not in cart
        }
      } else {
        this.addGiftButton.style.display = 'none';
        this.addGiftButton.disabled = true; // Ensure disabled if goal not met
      }
    }
  }

  /**
   * Add the free gift directly to cart using the specified variant ID
   */
  addGiftToCart() {
    // Check if we've reached the threshold
    if (this.fgwpCurrent < this.fgwpGoal || !this.variantId || this.isAddingToCart) {
      return;
    }

    // If gift is already in cart, don't add it again
    if (this.giftInCart) {
      return;
    }

    // Set loading state
    this.isAddingToCart = true;
    this.addGiftButton.classList.add('loading');
    this.addGiftButton.setAttribute('disabled', '');

    // Create FormData instance for cart add request
    const formData = new FormData();
    formData.append('id', this.variantId);
    formData.append('quantity', 1);

    // Add sections for cart rendering if cart component exists
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (cart) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map(function mapSection(section) {
          return section.id;
        })
      );
      formData.append('sections_url', window.location.pathname);
    }

    // Prepare fetch config
    const config = {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    };

    fetch(`/cart/add.js`, config)
      .then(function handleResponse(response) {
        return response.json();
      })
      .then(function processResponse(data) {
        if (data.status && data.status !== 'success') {
          throw new Error(data.message || 'Error adding gift to cart');
        }

        // Update gift in cart state
        this.giftInCart = true;

        // Change button text to indicate gift is in cart
        this.addGiftButton.textContent = 'Gift is in Cart';

        // Remove loading state
        this.addGiftButton.classList.remove('loading');
        this.addGiftButton.removeAttribute('disabled');

        // Get cart drawer and update its contents with the new cart data
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer.renderContents(data);
        }

        // Publish cart update event
        if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
          publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'fgwp-flask',
            productVariantId: this.variantId,
            cartData: data
          });
        }
      }.bind(this))
      .catch(function handleError(error) {
        console.error('Error adding gift to cart:', error);
        // Reset button on error
        this.addGiftButton.textContent = 'Try Again';
        this.addGiftButton.classList.remove('loading');
        this.addGiftButton.removeAttribute('disabled');
      }.bind(this))
      .finally(function finishLoading() {
        this.isAddingToCart = false;
      }.bind(this));
  }
}

if (!customElements.get('fgwp-flask')) {
  customElements.define('fgwp-flask', FgwpFlask);
}
